/**
 * API Error Handling Utilities
 * Provides standardized error handling for API responses
 */

/**
 * Handle API errors and return user-friendly messages
 * @param {Error} error - Axios error object
 * @returns {Object} Formatted error object
 */
export function handleApiError(error) {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        return { message: data.detail || 'Invalid request. Please check your input.', status };
      case 401:
        // Preserve server's error message if available (e.g., "Incorrect password")
        return { message: data.detail || 'Please log in to access this feature.', status };
      case 403:
        // Preserve server's error message (e.g., "Please verify your email")
        return { message: data.detail || 'You do not have permission to perform this action.', status };
      case 404:
        return { message: data.detail || 'The requested resource was not found.', status };
      case 429:
        return { message: data.detail || 'Too many requests. Please try again later.', status };
      case 500:
        return { message: 'Server error. Please try again later.', status };
      default:
        return { message: data.detail || 'An unexpected error occurred.', status };
    }
  } else if (error.request) {
    // Network error
    return { message: 'Unable to connect to the server. Please check your internet connection.', status: 0 };
  } else {
    // Other error
    return { message: 'An unexpected error occurred.', status: 0 };
  }
}

/**
 * Create a standardized error response
 * @param {string} message - Error message
 * @param {number} status - HTTP status code
 * @returns {Object} Formatted error object
 */
export function createError(message, status = 0) {
  return { message, status };
}

/**
 * Check if an error indicates authentication is required
 * @param {Object} error - Error object
 * @returns {boolean} True if error is authentication-related
 */
export function isAuthError(error) {
  return error.status === 401;
}

/**
 * Check if an error indicates rate limiting
 * @param {Object} error - Error object
 * @returns {boolean} True if error is rate limit-related
 */
export function isRateLimitError(error) {
  return error.status === 429;
}

export default handleApiError;